/* eslint-disable complexity */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { OfferContext, getLabelValue } from '@adac-mp/shared';
import { Button, Typography, CrossIcon } from '../../index';
import { useFocusableElements } from '../../hooks/useFocusableElements';
import { TrackingProps } from '../../utils/tracking/types';

interface IModalProps {
	isOpen: boolean;
	hidden?: boolean;
	wide?: boolean;
	fit?: boolean;
	noHeadline?: boolean;
	withSubmissionButtons?: boolean;
	title?: string | React.ReactNode;
	children: React.ReactNode;
	style?: React.CSSProperties;
	disabled?: boolean;
	submitButtonLabel?: string;
	cancelButtonLabel?: string;
	trackingData?: TrackingProps;
	buttonGroup?: (className?: string) => JSX.Element;
	onSubmit?: (contentRef?: HTMLDivElement | null) => void;
	onCancel?: () => void;
	onClose: () => void;
}

export const Modal = ({
	title,
	children,
	isOpen,
	fit = false,
	noHeadline = false,
	withSubmissionButtons = false,
	disabled = false,
	hidden,
	wide = false,
	trackingData,
	style = {},
	submitButtonLabel,
	cancelButtonLabel,
	buttonGroup,
	onSubmit = () => {},
	onCancel,
	onClose,
}: IModalProps) => {
	const { labels } = useContext(OfferContext);
	const modalRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const [renderModal, setRenderModal] = useState(false);
	const [isScrollable, setIsScrollable] = useState(false);

	const { onKeyDown } = useFocusableElements(modalRef, renderModal && !hidden);

	useEffect(() => {
		if (isOpen) {
			setRenderModal(true);
			document.body.classList.add('overflow-hidden');
		}
		return () => {
			document.body.classList.remove('overflow-hidden');
		};
	}, [isOpen]);

	return (
		<>
			{renderModal &&
				createPortal(
					<>
						<div
							role="dialog"
							aria-hidden={renderModal}
							aria-modal="true"
							aria-label={`modal-${title}`}
							className={`fixed z-[3000] top-0 left-0 w-full h-full bg-black/[.36] transition-all duration-300 ${
								hidden ? 'opacity-0' : `${isOpen ? 'animate-fadeIn' : 'animate-fadeOut'}`
							}`}
							onClick={onClose}>
							<div
								ref={modalRef}
								className="flex w-full h-full justify-center self-center md:max-w-desktop mx-auto"
								onKeyDown={(e) => {
									if (e.key === 'Escape') {
										e.preventDefault();
										e.stopPropagation();
										onClose();
										return;
									}
									onKeyDown(e);
								}}>
								<div
									className={`relative rounded bg-white flex justify-self-center max-h-[80vh] self-center flex-col w-full mx-1.5 md:max-h-screen ${
										fit && !wide && 'md:w-fit'
									} ${!fit && (wide ? 'md:max-w-8/12' : 'md:max-w-1/2')} ${
										isOpen ? 'animate-popupVerticalSlideIn' : 'animate-popupVerticalSlideOut'
									}`}
									style={style}
									onClick={(e) => e.stopPropagation()}
									onAnimationEnd={() => {
										if (!isOpen) {
											setRenderModal(false);
										} else {
											setTimeout(() => {
												setIsScrollable(
													contentRef.current?.offsetHeight !==
														contentRef.current?.scrollHeight,
												);
											});
										}
									}}>
									{!noHeadline && (
										<div className="flex rounded-t justify-between pt-4 px-4 pb-3 w-full sticky top-0 bg-white">
											<Typography component="p" size="lg" color="title" variant="title">
												{title}
											</Typography>
											<button
												data-cy="Modal: close"
												className="cursor-pointer inline-flex border-none bg-transparent h-fit opacity-80 transition-opacity hover:opacity-100"
												title="Close"
												aria-label="Close"
												onClick={onClose}>
												<CrossIcon fill="#121212" />
											</button>
										</div>
									)}
									<div ref={contentRef} className="p-1.5 overflow-y-auto overflow-x-hidden">
										{children}
									</div>
									{withSubmissionButtons && (
										<div
											className={`sticky bottom-0 bg-white p-1.5 flex flex-col gap-1.5 rounded-b ${
												isScrollable
													? 'border-solid border-l-0 border-b-0 border-r-0 border-t border-t-gray-600'
													: ''
											}`}>
											<Button
												fullWidth
												data-cy="Bestätigen-modal"
												disabled={disabled}
												tracking={trackingData}
												onClick={() => {
													onSubmit(contentRef.current);
												}}>
												{submitButtonLabel ||
													getLabelValue('mp.confirmButton', labels) ||
													'Bestätigen'}
											</Button>
											<Button
												fullWidth
												data-cy="Abbrechen-modal"
												variant="shy"
												onClick={() => {
													if (onCancel) {
														onCancel();
													} else {
														onClose();
													}
												}}>
												{cancelButtonLabel ||
													getLabelValue('mp.cancelButton', labels) ||
													'Abbrechen'}
											</Button>
										</div>
									)}
									{!withSubmissionButtons &&
										buttonGroup &&
										buttonGroup(
											isScrollable
												? 'border-t border-solid border-x-0 border-b-0 border-t-gray-600'
												: '',
										)}
								</div>
							</div>
						</div>
					</>,
					document.getElementById('portal') as HTMLElement,
				)}
		</>
	);
};
